import { ChevronRight, Menu } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Heading } from "../atoms/Heading";
import { cn } from "../lib/cn";

export interface TocItem {
	id: string;
	label: string;
	level: 1 | 2;
}

export interface HelpTableOfContentsProps {
	items: TocItem[];
	className?: string;
}

function scrollToSection(id: string): void {
	const el = document.getElementById(id);
	if (el) {
		el.scrollIntoView({ behavior: "smooth", block: "start" });
	}
}

function TocList({
	items,
	activeId,
	onSelect,
}: {
	items: TocItem[];
	activeId: string | null;
	onSelect: (id: string) => void;
}): ReactNode {
	return (
		<ul className="flex flex-col gap-0.5">
			{items.map((item) => {
				const isActive = item.id === activeId;
				return (
					<li key={item.id}>
						<button
							type="button"
							onClick={() => onSelect(item.id)}
							className={cn(
								"group flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm transition-colors",
								item.level === 2 ? "pl-4" : "pl-2",
								isActive
									? "bg-primary/10 font-medium text-primary"
									: "text-mutedForeground hover:bg-accent hover:text-foreground",
							)}
							aria-current={isActive ? "location" : undefined}
						>
							<ChevronRight
								className={cn(
									"h-3 w-3 shrink-0 transition-transform",
									isActive ? "rotate-90 text-primary" : "text-mutedForeground",
								)}
								aria-hidden="true"
							/>
							{item.label}
						</button>
					</li>
				);
			})}
		</ul>
	);
}

function HelpTableOfContents({
	items,
	className,
}: HelpTableOfContentsProps): ReactNode {
	const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);
	const [mobileOpen, setMobileOpen] = useState(false);
	const observerRef = useRef<IntersectionObserver | null>(null);
	// Track visibility ratios so we can pick the most visible section
	const visibilityMap = useRef<Map<string, number>>(new Map());

	const handleSelect = useCallback((id: string) => {
		setActiveId(id);
		scrollToSection(id);
		setMobileOpen(false);
	}, []);

	useEffect(() => {
		// Initialise visibility map
		for (const item of items) {
			visibilityMap.current.set(item.id, 0);
		}

		observerRef.current = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					visibilityMap.current.set(entry.target.id, entry.intersectionRatio);
				}

				// Pick the section with the highest visibility ratio
				let bestId: string | null = null;
				let bestRatio = 0;
				for (const [id, ratio] of visibilityMap.current.entries()) {
					if (ratio > bestRatio) {
						bestRatio = ratio;
						bestId = id;
					}
				}
				if (bestId !== null) {
					setActiveId(bestId);
				}
			},
			{ threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0] },
		);

		for (const item of items) {
			const el = document.getElementById(item.id);
			if (el) {
				observerRef.current.observe(el);
			}
		}

		return () => {
			observerRef.current?.disconnect();
		};
	}, [items]);

	return (
		<nav aria-label="Table of contents" className={cn("text-sm", className)}>
			{/* Desktop: always visible sticky list */}
			<div className="hidden lg:block">
				<Heading
					size="xs"
					as="h2"
					className="mb-3 uppercase tracking-wider text-mutedForeground"
				>
					On this page
				</Heading>
				<TocList items={items} activeId={activeId} onSelect={handleSelect} />
			</div>

			{/* Mobile: hamburger disclosure */}
			<div className="lg:hidden">
				<button
					type="button"
					aria-expanded={mobileOpen}
					aria-controls="toc-mobile-panel"
					onClick={() => setMobileOpen((prev) => !prev)}
					className="flex items-center gap-2 rounded border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
				>
					<Menu className="h-4 w-4" aria-hidden="true" />
					On this page
				</button>
				{mobileOpen && (
					<div
						id="toc-mobile-panel"
						className="mt-2 rounded border border-border bg-background p-3 shadow-sm"
					>
						<TocList
							items={items}
							activeId={activeId}
							onSelect={handleSelect}
						/>
					</div>
				)}
			</div>
		</nav>
	);
}
HelpTableOfContents.displayName = "HelpTableOfContents";

export default HelpTableOfContents;
export { HelpTableOfContents };
